import { useSeoMeta } from '@unhead/react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { LoginArea } from '@/components/auth/LoginArea';
import { ClaimReviewCard } from '@/components/ClaimReviewCard';
import { ManualGrantCard } from '@/components/ManualGrantCard';
import { DirectListingReviewCard } from '@/components/DirectListingReviewCard';
import { MerchantDebugPanel } from '@/components/MerchantDebugPanel';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useVerificationReviews, useIssueGrant } from '@/hooks/useVerificationAdmin';
import { useDirectListings } from '@/hooks/usePendingListings';
import { useApprovedListings, useManageApprovedListings } from '@/hooks/useApprovedListings';
import { isGrantIssuer } from '@/lib/businessVerify';
import { toast } from '@/hooks/useToast';
import { MapPin, ArrowLeft, ShieldCheck, RefreshCw, Lock } from 'lucide-react';

export default function AdminVerification() {
  useSeoMeta({
    title: 'Verification Admin — BCH Local',
    description: 'Review BCHNostr business verification claims and issue or revoke verified badges.',
  });

  const { user } = useCurrentUser();
  const isIssuer = !!user && isGrantIssuer(user.pubkey);

  const { data: reviews, isLoading, refetch, isRefetching } = useVerificationReviews();
  const { mutateAsync: issueGrant } = useIssueGrant();
  const { data: directListings, isLoading: directLoading, refetch: refetchDirect } = useDirectListings();
  const { data: approvedSet } = useApprovedListings();
  const { mutateAsync: manageApproved } = useManageApprovedListings();
  const [submittingPubkey, setSubmittingPubkey] = useState<string | null>(null);
  const [manualSubmitting, setManualSubmitting] = useState(false);

  const grouped = useMemo(() => {
    const list = reviews ?? [];
    return {
      pending: list.filter(r => r.state.status === 'PENDING'),
      verified: list.filter(r => r.state.status === 'VERIFIED'),
      other: list.filter(r => r.state.status === 'EXPIRED' || r.state.status === 'UNVERIFIED'),
    };
  }, [reviews]);

  const directGrouped = useMemo(() => {
    const approved = new Set(approvedSet ?? []);
    const list = directListings ?? [];
    return {
      pending: list.filter(l => !approved.has(l.pubkey)),
      approved: list.filter(l => approved.has(l.pubkey)),
    };
  }, [directListings, approvedSet]);

  // Approving a direct listing both adds it to the map and grants verification.
  const handleApproveListing = async (pubkey: string) => {
    setSubmittingPubkey(pubkey);
    try {
      await manageApproved({ pubkey, approved: true });
      await issueGrant({ businessPubkey: pubkey, revoked: false });
      toast({ title: 'Listing approved', description: 'It now appears on the map with a verified badge.' });
    } catch (e) {
      toast({
        title: 'Failed to approve listing',
        description: e instanceof Error ? e.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmittingPubkey(null);
    }
  };

  const handleRejectListing = async (pubkey: string) => {
    setSubmittingPubkey(pubkey);
    try {
      await manageApproved({ pubkey, approved: false });
      await issueGrant({ businessPubkey: pubkey, revoked: true });
      toast({ title: 'Listing removed', description: 'It no longer appears on the map.' });
    } catch (e) {
      toast({
        title: 'Failed to remove listing',
        description: e instanceof Error ? e.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmittingPubkey(null);
    }
  };

  const handleIssue = async (businessPubkey: string, txid?: string) => {
    setSubmittingPubkey(businessPubkey);
    try {
      await issueGrant({ businessPubkey, txid, revoked: false });
      toast({ title: 'Grant issued', description: 'The verified badge has been granted.' });
    } catch (e) {
      toast({
        title: 'Failed to issue grant',
        description: e instanceof Error ? e.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmittingPubkey(null);
    }
  };

  const handleManualGrant = async (businessPubkey: string) => {
    setManualSubmitting(true);
    try {
      await issueGrant({ businessPubkey, revoked: false });
      toast({ title: 'Grant issued', description: 'The verified badge has been granted manually.' });
    } catch (e) {
      toast({
        title: 'Failed to issue grant',
        description: e instanceof Error ? e.message : 'Please try again.',
        variant: 'destructive',
      });
      throw e;
    } finally {
      setManualSubmitting(false);
    }
  };

  const handleRevoke = async (businessPubkey: string) => {
    setSubmittingPubkey(businessPubkey);
    try {
      await issueGrant({ businessPubkey, revoked: true });
      toast({ title: 'Grant revoked', description: 'The verified badge has been removed.' });
    } catch (e) {
      toast({
        title: 'Failed to revoke grant',
        description: e instanceof Error ? e.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmittingPubkey(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="container flex items-center justify-between h-14 px-4 mx-auto max-w-3xl">
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
            <div className="flex items-center justify-center size-8 rounded-lg bg-bch">
              <MapPin className="size-4 text-bch-foreground" />
            </div>
            <span className="font-bold text-base sm:text-lg">BCH Local</span>
          </Link>
          <div className="flex items-center gap-2">
            {isIssuer && (
              <Button
                variant="ghost"
                size="icon"
                className="size-9 rounded-full"
                onClick={() => { refetch(); refetchDirect(); }}
                aria-label="Refresh"
              >
                <RefreshCw className={`size-4 ${isRefetching ? 'animate-spin' : ''}`} />
              </Button>
            )}
            <ThemeToggle />
          </div>
        </div>
      </header>

      <div className="container px-4 mx-auto max-w-3xl py-6 space-y-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="size-4" />
          Back to Map
        </Link>

        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center size-12 rounded-2xl bg-bch/10">
            <ShieldCheck className="size-6 text-bch" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Verification Admin</h1>
            <p className="text-sm text-muted-foreground">Review payment claims and manage verified badges</p>
          </div>
        </div>

        {/* Access control */}
        {!user ? (
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center space-y-4">
              <Lock className="size-10 text-muted-foreground/40 mx-auto" />
              <p className="text-muted-foreground max-w-sm mx-auto">
                Log in with a trusted issuer account to review verification claims.
              </p>
              <div className="flex justify-center">
                <LoginArea className="max-w-60" />
              </div>
            </CardContent>
          </Card>
        ) : !isIssuer ? (
          <Card className="border-destructive/40 bg-destructive/5">
            <CardContent className="py-12 px-8 text-center space-y-3">
              <Lock className="size-10 text-destructive/50 mx-auto" />
              <p className="font-medium">Access denied</p>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                Your account is not a trusted verification issuer. Only BCHNostr issuers can review
                claims and grant badges.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Tabs defaultValue="listings" className="space-y-4">
            <TabsList className="w-full">
              <TabsTrigger value="listings" className="flex-1">
                Direct listings ({directGrouped.pending.length})
              </TabsTrigger>
              <TabsTrigger value="verification" className="flex-1">
                Verification ({grouped.pending.length})
              </TabsTrigger>
              <TabsTrigger value="debug" className="flex-1">
                Debug
              </TabsTrigger>
            </TabsList>

            {/* Direct BCH Local listings */}
            <TabsContent value="listings" className="space-y-4">
              {directLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Card key={i}>
                      <CardContent className="py-4 px-4 space-y-3">
                        <div className="flex items-center gap-3">
                          <Skeleton className="size-10 rounded-lg" />
                          <div className="space-y-1.5 flex-1">
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-3 w-48" />
                          </div>
                        </div>
                        <Skeleton className="h-9 w-full" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <>
                  <div>
                    <h3 className="text-sm font-semibold mb-2">Pending approval ({directGrouped.pending.length})</h3>
                    <div className="space-y-3">
                      {directGrouped.pending.length === 0 ? (
                        <Card className="border-dashed">
                          <CardContent className="py-10 px-8 text-center">
                            <p className="text-muted-foreground text-sm">No listings waiting for approval. 🎉</p>
                          </CardContent>
                        </Card>
                      ) : (
                        directGrouped.pending.map(listing => (
                          <DirectListingReviewCard
                            key={listing.pubkey}
                            listing={listing}
                            approved={false}
                            onApprove={handleApproveListing}
                            onReject={handleRejectListing}
                            isSubmitting={submittingPubkey === listing.pubkey}
                          />
                        ))
                      )}
                    </div>
                  </div>

                  {directGrouped.approved.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold mb-2">Approved ({directGrouped.approved.length})</h3>
                      <div className="space-y-3">
                        {directGrouped.approved.map(listing => (
                          <DirectListingReviewCard
                            key={listing.pubkey}
                            listing={listing}
                            approved
                            onApprove={handleApproveListing}
                            onReject={handleRejectListing}
                            isSubmitting={submittingPubkey === listing.pubkey}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            {/* BCHNostr verification claims */}
            <TabsContent value="verification" className="space-y-4">
              <ManualGrantCard onIssue={handleManualGrant} isSubmitting={manualSubmitting} />
              {isLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Card key={i}>
                      <CardContent className="py-4 px-4 space-y-3">
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-9 w-full" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Tabs defaultValue="pending" className="space-y-4">
                  <TabsList className="w-full">
                    <TabsTrigger value="pending" className="flex-1">Pending ({grouped.pending.length})</TabsTrigger>
                    <TabsTrigger value="verified" className="flex-1">Verified ({grouped.verified.length})</TabsTrigger>
                    <TabsTrigger value="other" className="flex-1">Other ({grouped.other.length})</TabsTrigger>
                  </TabsList>

                  {(['pending', 'verified', 'other'] as const).map(key => (
                    <TabsContent key={key} value={key} className="space-y-3">
                      {grouped[key].length === 0 ? (
                        <Card className="border-dashed">
                          <CardContent className="py-10 px-8 text-center">
                            <p className="text-muted-foreground text-sm">
                              {key === 'pending'
                                ? 'No claims waiting for review. 🎉'
                                : key === 'verified'
                                  ? 'No verified businesses yet.'
                                  : 'Nothing here.'}
                            </p>
                          </CardContent>
                        </Card>
                      ) : (
                        grouped[key].map(review => (
                          <ClaimReviewCard
                            key={review.businessPubkey}
                            review={review}
                            onIssue={handleIssue}
                            onRevoke={handleRevoke}
                            isSubmitting={submittingPubkey === review.businessPubkey}
                          />
                        ))
                      )}
                    </TabsContent>
                  ))}
                </Tabs>
              )}
            </TabsContent>

            {/* Debug: look up any pubkey's events on the discovery relays */}
            <TabsContent value="debug" className="space-y-4">
              <MerchantDebugPanel />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
